<template>
    <TransitionRoot as="template" :show="open">
        <Dialog
            as="div"
            static
            class="fixed inset-0 overflow-hidden z-50"
            @close="open = false"
            :open="open"
        >
            <div
                id="health-profile-side-bar"
                class="absolute inset-0 overflow-hidden"
            >
                <DialogOverlay class="absolute inset-0" />
                <div class="fixed inset-y-0 pl-8 max-w-full right-0 flex">
                    <TransitionChild
                        as="template"
                        enter="transform transition ease-in-out duration-500"
                        enter-from="translate-x-full"
                        enter-to="translate-x-0"
                        leave="transform transition ease-in-out duration-500"
                        leave-from="translate-x-0"
                        leave-to="translate-x-full"
                    >
                        <div class="w-screen max-w-md">
                            <div
                                class="border-l border-gray-200 h-full flex flex-col bg-white shadow-xl"
                            >
                                <div
                                    class="bg-nutreance-green py-4 px-4 sm:px-6"
                                >
                                    <div
                                        class="flex items-center justify-between"
                                    >
                                        <DialogTitle
                                            class="text-lg font-medium text-white"
                                        >
                                            <div class="flex items-center">
                                                <DocumentSearchIcon
                                                    class="h-6 w-6 mr-2"
                                                />
                                                Your Health Profile
                                            </div>
                                        </DialogTitle>
                                        <div class="ml-3 h-7 flex items-center">
                                            <button
                                                type="button"
                                                data-test="close"
                                                class="text-white bg-nutreance-green hover:bg-nutreance-green-darker p-1 rounded-md focus:outline-none"
                                                @click="open = false"
                                            >
                                                <span class="sr-only"
                                                    >Close panel</span
                                                >
                                                <XIcon
                                                    class="h-6 w-6"
                                                    aria-hidden="true"
                                                />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div class="flex-1 h-0 overflow-y-auto">
                                    <div class="px-6 mt-3 mb-2">
                                        <div
                                            class="shrink-0 my-6 mx-auto w-48 md:w-60"
                                        >
                                            <a
                                                href="/health-profile/questions"
                                                class="focus:outline-none"
                                            >
                                                <img
                                                    class="mx-auto"
                                                    :src="imgSrc('1x')"
                                                    :srcset="imgSrcset"
                                                    :alt="imgAlt"
                                                />
                                            </a>
                                        </div>

                                        <div class="prose">
                                            <p
                                                class="mt-3 text-gray-700 text-center"
                                            >
                                                {{
                                                    product
                                                        .health_profile_product_description
                                                        .introduction
                                                }}
                                            </p>

                                            <p
                                                class="mt-3 text-gray-700 text-center"
                                            >
                                                Take this
                                                <a
                                                    class="!text-nutreance-green hover:!text-nutreance-green-lighter !font-semibold !no-underline"
                                                    href="/health-profile/questions"
                                                    >free 5 minute health
                                                    profile</a
                                                >
                                                to evaluate your risk for
                                                medical conditions that may have
                                                gone undiagnosed until now.
                                            </p>
                                        </div>

                                        <a
                                            role="button"
                                            href="/health-profile/questions"
                                            class="mt-6 inline-flex justify-center items-center px-6 py-3 border border-transparent rounded-md shadow-sm font-medium text-white bg-nutreance-green hover:bg-nutreance-green-darker focus:outline-none w-full"
                                        >
                                            Start My Profile
                                            <ArrowRightIcon
                                                class="h-5 w-5 ml-2"
                                                aria-hidden="true"
                                            />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref } from "vue";
import {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import { XIcon } from "@heroicons/vue/outline";
import { ArrowRightIcon, DocumentSearchIcon } from "@heroicons/vue/solid";
import { useStaticAsset } from "../../functions/useStaticAsset";
import type { AppEmitter } from "../../emitter";

const props = defineProps<{
    openAutomatically?: string;
    product?: any;
}>();

const emitter = inject<AppEmitter>("mitt")!;
const { asset } = useStaticAsset();
const open = ref(false);
const imgAlt = computed(
    () => `${props.product.name} (${props.product.description})`,
);
const imgSrcset = computed(
    () => `${imgSrc("1x")} 1x, ${imgSrc("2x")} 2x, ${imgSrc("3x")} 3x`,
);

function imgSrc(size: string) {
    const slug = props.product.slug;
    const upc = props.product.default_product_option.upc;
    const image = "bottle-spilled-front@1x.jpg".replace("@1x", `@${size}`);

    return asset(`/images/products/${slug}/${upc}/${image}`);
}

onMounted(() => {
    if (props.openAutomatically) {
        setTimeout(() => (open.value = true), 3000);
    }

    emitter.on("shippingAddressCompleted", () =>
        setTimeout(() => (open.value = true), 3000),
    );
});
</script>
